import { useId, useState } from 'react'
import type { Moment } from '@domain/events.ts'
import { es } from '../strings/es.ts'

/**
 * Creating a task by tapping, which until now could only be done by describing
 * it to the assistant or by editing the seed.
 *
 * It takes the same six fields the assistant's vocabulary takes, and for the
 * same reason: whatever comes out of here goes through `defineTask`, so the
 * rules are the domain's either way. A routine that pays coins is refused
 * identically whether a model proposed it or a parent typed it — which is why
 * the coins box simply is not there for a routine. The rule is not hidden; it
 * is the shape of the form.
 */
export function NewTask({
  onCreate,
}: {
  onCreate: (task: {
    kind: 'routine' | 'mission'
    moment: Moment | undefined
    name: string
    steps: readonly string[]
    coins: number
  }) => Promise<string | null>
}) {
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<'routine' | 'mission'>('mission')
  const [moment, setMoment] = useState<Moment>('afternoon')
  const [name, setName] = useState('')
  const [steps, setSteps] = useState<readonly string[]>([''])
  const [coins, setCoins] = useState(5)
  const [note, setNote] = useState<string | null>(null)
  const nameId = useId()
  const coinsId = useId()

  const create = async (): Promise<void> => {
    if (name.trim().length === 0) {
      setNote(es.newTask.needName)
      return
    }
    const error = await onCreate({
      kind,
      moment: kind === 'routine' ? moment : undefined,
      name: name.trim(),
      steps: steps.map((s) => s.trim()).filter((s) => s.length > 0),
      coins: kind === 'routine' ? 0 : coins,
    })
    setNote(error ?? es.newTask.created)
    if (!error) {
      setName('')
      setSteps([''])
      setOpen(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setNote(null)
          setOpen(true)
        }}
        className="border-rule mt-3 w-full rounded-xl border-2 border-dashed px-6 py-4 font-bold"
      >
        + {es.newTask.open}
      </button>
    )
  }

  return (
    <section className="border-rule bg-surface mt-3 rounded-xl border-2 p-4">
      <h4 className="m-0 mb-3 text-base font-extrabold">{es.newTask.title}</h4>

      <fieldset className="m-0 mb-3 border-0 p-0">
        <legend className="mb-2 text-sm font-bold">{es.newTask.kind}</legend>
        <div className="flex gap-2">
          {(['routine', 'mission'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={kind === option}
              onClick={() => {
                setKind(option)
              }}
              className={`border-rule flex-1 rounded-lg border-2 px-4 py-3 font-bold ${
                kind === option ? 'bg-teal-soft text-teal border-teal' : ''
              }`}
            >
              {option === 'routine' ? es.newTask.routine : es.newTask.mission}
            </button>
          ))}
        </div>
        <p className="text-muted mt-2 mb-0 text-sm">{es.newTask.kindHelp}</p>
      </fieldset>

      {kind === 'routine' ? (
        <fieldset className="m-0 mb-3 border-0 p-0">
          <legend className="mb-2 text-sm font-bold">{es.newTask.moment}</legend>
          <div className="flex flex-wrap gap-2">
            {(['morning', 'afternoon', 'evening'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={moment === option}
                onClick={() => {
                  setMoment(option)
                }}
                className={`border-rule rounded-lg border-2 px-4 py-2 text-sm font-bold ${
                  moment === option ? 'bg-teal-soft text-teal border-teal' : ''
                }`}
              >
                {es.newTask[option]}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}

      <label htmlFor={nameId} className="mb-1 block text-sm font-bold">
        {es.config.name}
      </label>
      <input
        id={nameId}
        value={name}
        onChange={(e) => {
          setName(e.target.value)
        }}
        className="border-rule bg-surface mb-3 w-full rounded-lg border-2 p-3"
      />

      <p className="mb-1 text-sm font-bold">{es.config.steps}</p>
      <ul className="m-0 mb-2 grid list-none gap-2 p-0">
        {steps.map((step, index) => (
          // The list is short and positional; its index is the identity a
          // parent sees, which is exactly what makes a key by index right here.
          <li key={index} className="m-0 flex gap-2">
            <input
              value={step}
              aria-label={`${es.config.steps} ${String(index + 1)}`}
              onChange={(e) => {
                setSteps(steps.map((s, i) => (i === index ? e.target.value : s)))
              }}
              className="border-rule bg-surface min-w-0 flex-1 rounded-lg border-2 p-3"
            />
            <button
              type="button"
              aria-label={es.config.removeStep}
              onClick={() => {
                setSteps(steps.filter((_, i) => i !== index))
              }}
              className="border-rule rounded-lg border-2 px-4 font-bold"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        disabled={steps.length >= 8}
        onClick={() => {
          setSteps([...steps, ''])
        }}
        className="text-teal mb-3 text-sm font-bold underline underline-offset-4 disabled:opacity-40"
      >
        {es.config.addStep}
      </button>

      {kind === 'mission' ? (
        <>
          <label htmlFor={coinsId} className="mb-1 block text-sm font-bold">
            {es.newTask.coins}
          </label>
          <input
            id={coinsId}
            type="number"
            min={0}
            max={50}
            value={coins}
            onChange={(e) => {
              setCoins(Number(e.target.value))
            }}
            className="border-rule bg-surface mb-3 w-28 rounded-lg border-2 p-3"
          />
        </>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            void create()
          }}
          className="bg-teal rounded-lg px-5 py-3 font-bold text-white"
        >
          {es.newTask.create}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false)
          }}
          className="border-rule rounded-lg border-2 px-5 py-3 font-bold"
        >
          {es.config.close}
        </button>
      </div>

      {note ? (
        <p role="status" className="text-muted m-0 mt-3 text-sm font-bold">
          {note}
        </p>
      ) : null}
    </section>
  )
}
